import { action, makeObservable, observable, runInAction } from 'mobx';

import {
  Parent,
  ParentReview,
  ParentReviewService,
  ParentService,
  SitterBlockParentService,
} from 'us-web-services';

export const RECOMMENDATION_YES = 1;
export const RECOMMENDATION_OTHERS = 2;
export const RECOMMENDATION_NONE = 3;
export const VISIBILITY_PUBLIC = 1;
export const VISIBILITY_PRIVATE = 2;

// Re-export so consumers don't need to import ParentReview from us-web-services directly.
export type { ParentReview } from 'us-web-services';

// Shared state for the sitter-reviewing-parent flow rooted at /parent/:id/feedback
// and its sub-states (safety, message, availability, success). Angular used a
// singleton service for this; we use a MobX store.
export class ReviewFlowStore {
  parent: Parent | null = null;
  review: ParentReview | null = null;
  reviewVisibility: boolean = true;
  loading: boolean = false;

  constructor() {
    makeObservable(this, {
      parent: observable,
      review: observable,
      reviewVisibility: observable,
      loading: observable,
      loadParentAndReview: action,
      setReview: action,
      setVisibility: action,
      saveReview: action,
      blockParent: action,
      unblockParent: action,
    });
  }

  setReview = (partial: Partial<ParentReview>): void => {
    this.review = { ...(this.review ?? {}), ...partial };
  };

  setVisibility = (visible: boolean): void => {
    this.reviewVisibility = visible;
  };

  async loadParentAndReview(parentId: number, sitterId: number): Promise<void> {
    this.loading = true;
    try {
      const [parentResp, reviewResp] = await Promise.all([
        ParentService.get(parentId, { params: { sitterId, userId: sitterId } }),
        ParentReviewService.getByFilter({ params: { parentId, sitterId } }),
      ]);

      const parentData = parentResp.data?.data ?? null;
      const existing = (reviewResp.data?.data ?? [])[0];

      runInAction(() => {
        this.parent = parentData;
        if (existing) {
          this.review = existing;
          this.reviewVisibility = existing.visibility?.id === VISIBILITY_PUBLIC;
        } else {
          this.review = {
            recommendation: undefined,
            parent: { id: parentId },
            sitter: { id: sitterId },
          };
          this.reviewVisibility = true;
        }
      });
    } catch (err) {
      console.warn('[ReviewFlowStore] load failed', err);
    } finally {
      runInAction(() => { this.loading = false; });
    }
  }

  async saveReview(): Promise<ParentReview | null> {
    if (!this.review) return null;
    const body: ParentReview = {
      ...this.review,
      visibility: { id: this.reviewVisibility ? VISIBILITY_PUBLIC : VISIBILITY_PRIVATE },
    };

    const resp = body.id
      ? await ParentReviewService.patch(body.id, body)
      : await ParentReviewService.create(body);
    const saved = (resp.data?.data ?? body) as ParentReview;
    runInAction(() => { this.review = saved; });
    return saved;
  }

  async blockParent(): Promise<void> {
    if (!this.parent?.id || !this.review?.sitter?.id) return;
    const resp = await SitterBlockParentService.create({
      sitterId: this.review.sitter.id,
      parentId: this.parent.id,
    });
    const newId = resp.data?.data?.id ?? null;
    runInAction(() => {
      if (this.parent) {
        this.parent = {
          ...this.parent,
          relationship: {
            ...(this.parent.relationship ?? {}),
            blocked: true,
            blockedId: newId,
          },
        };
      }
    });
  }

  async unblockParent(): Promise<void> {
    if (!this.parent?.relationship?.blockedId) return;
    const blockedId = this.parent.relationship.blockedId;
    await SitterBlockParentService.delete(blockedId);
    runInAction(() => {
      if (this.parent) {
        this.parent = {
          ...this.parent,
          relationship: {
            ...(this.parent.relationship ?? {}),
            blocked: false,
            blockedId: null,
          },
        };
      }
    });
  }

  isValidForParent(parentId: number): boolean {
    return !!this.review?.parent?.id && this.review.parent.id === parentId;
  }
}
